import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import { Observable, Subject, map, of, switchMap, takeUntil } from 'rxjs';
import { AccountOnHoldSimpleComponent, InvalidFieldComponent } from 'src/app/shared/components';
import { LoginFormGroupType } from 'src/app/shared/components/login-form/models/login-form';
import { CustomerService, SessionService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { AutoPayCargoCreditComponent } from '../auto-pay-cargo-credit/auto-pay-cargo-credit.component';
import { AutoPayCustomerReferenceComponent } from '../auto-pay-customer-reference/auto-pay-customer-reference.component';
import { AutoPayLoginComponent } from '../auto-pay-login/login-auto-pay.component';
import { MAWBSubscriptionFormGroupType } from '../mawb-subscription/models/mawb-subscription-form';
import { AwbSubscriptionService } from '../services/awb-subscription.service';
import { AutoPayFormErrorJSON, AutoPayFormGroupType } from './models/auto-pay-form';
import { autoPayFormErrors } from './utils/form-errors';

@Component({
    selector: 'app-auto-pay',
    standalone: true,
    imports: [
        MatCheckboxModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        InvalidFieldComponent,
        RouterModule,
        AutoPayCargoCreditComponent,
        AutoPayLoginComponent,
        AutoPayCustomerReferenceComponent,
        AccountOnHoldSimpleComponent,
    ],

    templateUrl: './auto-pay.component.html',
    styleUrl: './auto-pay.component.scss',
})
export class AutoPayComponent implements OnInit, OnDestroy {
    public showEmail: boolean = false;
    public information =
        "AutoPay allows you to enroll to pay ISC (and all applicable associated fees) at the MAWB level. When the MAWB ISC charges are available, your ISC payment will be automatically generated and we'll email you a receipt";
    public autoPayForm: AutoPayFormGroupType;
    public formErrors: AutoPayFormErrorJSON;
    public showNotes: boolean;
    public isAuthenticated = false;
    public loginForm: LoginFormGroupType;
    public isLoading = false;
    public isRequestor: boolean;

    private selectAutoPayFirst = false;
    private unsubscribe$: Subject<void>;
    private subscriptionForm: MAWBSubscriptionFormGroupType;
    private isArrivalSubscriptionExist = false;
    public isAccountOnHold: boolean = false;

    get email(): AbstractControl<string> | null {
        return this.autoPayForm?.get('email');
    }

    get isSelected(): AbstractControl<boolean> | null {
        return this.autoPayForm?.get('isSelected');
    }

    constructor(
        private awbSubscriptionService: AwbSubscriptionService,
        private fb: NonNullableFormBuilder,
        private sessionService: SessionService,
        private customerService: CustomerService,
        private featureRestrictionsService: FeatureRestrictionsService
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.formErrors = autoPayFormErrors;
        this.isArrivalSubscriptionExist = this.awbSubscriptionService.getIsArrivalSubscriptionExist();
    }

    ngOnInit(): void {
        this.subscribeAccountStatus();
        this.subscribeIsAuthenticatedAndRequestor();
        this.createForm();
        this.subscribeSubscription();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.autoPayForm = this.fb.group({
            isSelected: [false],
            email: [''],
        });
        this.awbSubscriptionService.setAutoPayForm(this.autoPayForm);
    }

    onKeyEmail(): void {
        this.awbSubscriptionService.setAutoPayForm(this.autoPayForm);
    }

    subscribeSubscription(): void {
        this.awbSubscriptionService
            .getSubscriptionForm()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((subscriptionForm: MAWBSubscriptionFormGroupType) => {
                this.subscriptionForm = subscriptionForm;
                this.setEmail();
            });
    }

    activeAutoPay(isSelected: boolean): void {
        this.showEmail = isSelected;
        this.showNotes = false;
        const emailSubscriptionForm = this.subscriptionForm?.get('email');
        const isSelectedSubscriptionForm = this.subscriptionForm?.get('isSelected');
        if (isSelected) {
            this.email?.setValidators([Validators.required, CustomValidators.regex(new RegExp(CustomPatterns.EMAIL))]);
            if (!this.isArrivalSubscriptionExist && !this.selectAutoPayFirst) {
                if (!(isSelectedSubscriptionForm?.value && emailSubscriptionForm?.valid)) {
                    this.selectAutoPayFirst = true;
                }
                this.subscriptionForm?.patchValue({
                    isSelected: true,
                });
                if (!emailSubscriptionForm?.value) {
                    this.showNotes = true;
                }
            }
        } else {
            this.email?.clearValidators();
            if (this.selectAutoPayFirst) {
                this.selectAutoPayFirst = false;
                this.subscriptionForm.reset();
                emailSubscriptionForm?.setErrors(null);
            }
        }
        this.email?.updateValueAndValidity();
        this.autoPayForm?.updateValueAndValidity();
        this.awbSubscriptionService.selectAutoPayFirst(this.selectAutoPayFirst);
        this.awbSubscriptionService.setSubscriptionForm(this.subscriptionForm);
        this.awbSubscriptionService.setAutoPayForm(this.autoPayForm);
    }

    subscribeIsAuthenticatedAndRequestor(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(
                switchMap((isAuthenticated) => {
                    if (isAuthenticated) {
                        return this.isRequestorUser().pipe(map((isRequestor) => [isAuthenticated, isRequestor]));
                    }
                    return of([isAuthenticated, false]);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ([isAuthenticated, isRequestor]) => {
                    this.isAuthenticated = isAuthenticated;
                    this.isRequestor = isRequestor;
                    if (this.isAuthenticated) {
                        this.setEmail();
                    }
                },
            });
    }

    getEmailSubscription(): string {
        return this.subscriptionForm?.get('email')?.value || '';
    }

    setEmail(): void {
        if (!this.email?.value && this.isAuthenticated) {
            const emailCustomer = this.getEmailSubscription() || this.customerService.getCustomerEmail();
            if (emailCustomer) {
                this.autoPayForm?.patchValue({ email: emailCustomer });
                this.autoPayForm?.updateValueAndValidity();
            }
        }
    }

    isRequestorUser(): Observable<boolean> {
        return this.awbSubscriptionService.isCompany().pipe(
            switchMap((isCompany) => {
                if (!isCompany) {
                    return of(false);
                }
                return this.awbSubscriptionService.isRequestor();
            })
        );
    }

    subscribeAccountStatus(): void {
        this.featureRestrictionsService
            .isAccountOnHold()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAccountOnHold) => {
                    this.isAccountOnHold = isAccountOnHold;
                },
            });
    }
}
